import { motion } from "framer-motion";
import {
  X,
  Clock,
  DollarSign,
  Hash,
  Shield,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  Copy,
  Check,
  Users,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useRun, useAgents, useFeedbackHistory } from "../../api/hooks.js";
import { useFeedbackStore } from "../../stores/feedback.js";
import { StatusBadge } from "../common/StatusBadge.js";
import { FeedbackTimeline } from "../feedback/FeedbackTimeline.js";
import { AgentActivityView } from "./AgentActivityView.js";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect } from "react";

const AGENT_COLORS = ["#8000ff", "#00c8ff", "#00dc64", "#ff0080", "#f59e0b", "#a855f7"];

interface RunDetailPanelProps {
  runId: string;
  onClose: () => void;
}

export function RunDetailPanel({ runId, onClose }: RunDetailPanelProps) {
  const { data: run } = useRun(runId);
  const messages = useFeedbackStore((s) => s.messages);
  const loadHistorical = useFeedbackStore((s) => s.loadHistorical);
  const [activeTab, setActiveTab] = useState<"timeline" | "agents" | "breakdown" | "output">("timeline");
  const [copied, setCopied] = useState(false);
  const { data: historicalFeedback } = useFeedbackHistory(runId);

  // Auto-switch to output tab when run completes with output
  useEffect(() => {
    if (run?.output && run?.status === "completed") {
      setActiveTab("output");
    }
  }, [run?.output, run?.status]);

  // Load historical feedback from DB into the store
  useEffect(() => {
    if (historicalFeedback && historicalFeedback.length > 0) {
      loadHistorical(
        historicalFeedback.map((msg: any) => ({
          id: msg.id,
          type: msg.type ?? msg.feedbackType,
          source: msg.source ?? { agentId: msg.sourceAgentId ?? "unknown", name: msg.sourceAgentId ?? "unknown" },
          payload: msg.payload,
          timestamp: msg.timestamp,
          runId: msg._meta?.runId ?? runId,
        })),
      );
    }
  }, [historicalFeedback, loadHistorical]);

  if (!run) {
    return (
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed right-0 top-0 bottom-0 w-96 bg-surface-light border-l border-border-subtle/20 z-40 flex items-center justify-center"
      >
        <Loader2 className="w-5 h-5 text-text-muted animate-spin" />
      </motion.div>
    );
  }

  const runMessages = messages.filter((m) => m.runId === runId);

  const duration = run.completedAt
    ? run.completedAt - run.startedAt
    : Date.now() - run.startedAt;

  const StatusIcon =
    run.status === "completed"
      ? CheckCircle2
      : run.status === "failed" || run.status === "cancelled"
        ? XCircle
        : run.status === "running"
          ? Loader2
          : Clock;

  const statusIconColor =
    run.status === "completed"
      ? "text-brand-green"
      : run.status === "failed" || run.status === "cancelled"
        ? "text-brand-pink"
        : run.status === "running"
          ? "text-brand-cyan"
          : "text-text-muted";

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed right-0 top-0 bottom-0 w-[420px] bg-surface-light border-l border-border-subtle/20 z-40 flex flex-col shadow-2xl shadow-black/40"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-border-subtle/15 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <StatusIcon
              className={`w-5 h-5 ${statusIconColor} ${run.status === "running" ? "animate-spin" : ""}`}
            />
            <StatusBadge status={run.status} />
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-[color,transform] duration-200 active:scale-[0.96] p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Input */}
        <p className="text-sm text-text-primary leading-relaxed mb-3 line-clamp-3">
          {run.input}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Clock} label="Duration" value={formatDuration(duration)} />
          <Stat icon={DollarSign} label="Cost" value={`$${run.totalCostUsd.toFixed(4)}`} />
          <Stat
            icon={Hash}
            label="Tokens"
            value={formatTokens(run.totalTokens.input + run.totalTokens.output)}
          />
        </div>

        {(() => {
          const entry = run.entryAgent;
          const others = (run.participatingAgents ?? []).filter((a) => a !== entry);
          const chain = entry
            ? others.length
              ? `${entry} → ${others.join(" → ")}`
              : entry
            : (run.participatingAgents ?? []).join(" → ");
          return chain ? (
            <div className="flex items-center gap-1.5 mt-3">
              <Users className="w-3.5 h-3.5 text-text-muted" />
              <span className="text-[11px] text-text-muted font-medium">{chain}</span>
            </div>
          ) : null;
        })()}

        {run.supervised && (
          <div className="flex items-center gap-1.5 mt-3">
            <Shield className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] text-amber-400 font-medium">Supervised mode</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex px-5 border-b border-border-subtle/15 shrink-0">
        <TabButton
          active={activeTab === "timeline"}
          onClick={() => setActiveTab("timeline")}
          label="Timeline"
        />
        <TabButton
          active={activeTab === "agents"}
          onClick={() => setActiveTab("agents")}
          label="Agents"
        />
        <TabButton
          active={activeTab === "breakdown"}
          onClick={() => setActiveTab("breakdown")}
          label="Breakdown"
        />
        <TabButton
          active={activeTab === "output"}
          onClick={() => setActiveTab("output")}
          label={`Output${run.output ? "" : ""}`}
        />
        {run.output && (
          <div className="ml-auto flex items-center pr-1">
            <div className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === "timeline" && (
          <FeedbackTimeline
            filters={new Set()}
            search=""
            runId={runId}
          />
        )}
        {activeTab === "agents" && (
          <AgentActivityView runId={runId} />
        )}
        {activeTab === "breakdown" && (
          <CostBreakdown run={run} />
        )}
        {activeTab === "output" && (
          <div className="h-full overflow-y-auto p-5">
            {run.output ? (
              <div className="relative">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(run.output ?? "");
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="absolute top-0 right-0 p-1.5 rounded-lg bg-surface/50 hover:bg-surface/80 text-text-muted hover:text-text-primary transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
                  title="Copy output"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-brand-green" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <pre className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap font-mono pr-8">
                  {run.output}
                </pre>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <FileText className="w-8 h-8 text-text-muted/40 mb-3" />
                <p className="text-sm text-text-muted">No output yet</p>
                <p className="text-xs text-text-muted/60 mt-1">Output will appear when the run completes</p>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="bg-surface/30 rounded-lg px-3 py-2">
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="w-3 h-3 text-text-muted" />
        <span className="text-[10px] text-text-muted">{label}</span>
      </div>
      <div className="text-sm text-text-primary font-mono tabular-nums">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-xs font-medium transition-[color,border-color,transform] duration-200 active:scale-[0.96] border-b-2 ${
        active
          ? "text-brand-purple border-brand-purple"
          : "text-text-muted hover:text-text-primary border-transparent"
      }`}
    >
      {label}
    </button>
  );
}

function CostBreakdown({ run }: { run: any }) {
  // Show token breakdown
  const data = [
    { name: "Input", value: run.totalTokens.input, color: "#8000ff" },
    { name: "Output", value: run.totalTokens.output, color: "#00c8ff" },
    { name: "Cached", value: run.totalTokens.cached ?? 0, color: "#00dc64" },
  ].filter((d) => d.value > 0);

  return (
    <div className="p-5 space-y-5">
      {/* Token donut */}
      <div>
        <h3 className="text-xs text-text-muted font-medium mb-3">Token Usage</h3>
        {data.length > 0 ? (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={100} height={100}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={40}
                  strokeWidth={0}
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#111113",
                    border: "1px solid rgba(39, 39, 42, 0.3)",
                    borderRadius: "8px",
                    fontSize: "11px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5">
              {data.map((d) => (
                <div key={d.name} className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: d.color }}
                  />
                  <span className="text-xs text-text-secondary">{d.name}</span>
                  <span className="text-xs text-text-primary font-mono tabular-nums">
                    {formatTokens(d.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-text-muted">No token data available</p>
        )}
      </div>

      {/* Run metadata */}
      <div>
        <h3 className="text-xs text-text-muted font-medium mb-2">Details</h3>
        <div className="space-y-1.5 text-xs">
          <MetaRow label="Run ID" value={run.id} mono />
          <MetaRow label="Topology" value={run.topologyInstanceId} mono />
          {run.startedAt && (
            <MetaRow
              label="Started"
              value={new Date(run.startedAt).toLocaleString()}
            />
          )}
          {run.completedAt && (
            <MetaRow
              label="Completed"
              value={new Date(run.completedAt).toLocaleString()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className={`text-text-secondary truncate max-w-[200px] tabular-nums ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
